// apps/app/src/features/auth/auth-layout.tsx
//
// The frame every signed-out page shares: a white panel with the form on the
// left, and on large screens a dark panel on the right showing what the product
// does — the scan, the report and the lead it turns into. It shows the product
// rather than a testimonial: there is no customer quote to put there honestly.

import { forwardRef, useState, type ComponentProps, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Globe, type LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LANGUAGES, isLanguage } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import face from '@/assets/auth-face.jpg';

interface AuthLayoutProps {
  /** Top-right: e.g. "Already have an account? Sign in". */
  aside?: { text: string; action: string; to: string } | undefined;
  children: ReactNode;
}

export function AuthLayout({ aside, children }: AuthLayoutProps) {
  const { t } = useTranslation('auth');

  return (
    <div className="min-h-screen bg-sidebar lg:grid lg:grid-cols-[minmax(0,1.08fr)_minmax(0,1fr)] lg:p-3">
      <div className="flex min-h-screen flex-col bg-background lg:min-h-0 lg:rounded-2xl">
        <header className="flex items-center gap-3 px-5 pt-5 sm:px-8 sm:pt-7">
          <Link to="/login" className="flex items-center gap-2.5 rounded-md" aria-label="SkinVizo">
            <span className="grid size-9 place-items-center rounded-[10px] bg-foreground text-[13px] font-semibold text-background">SV</span>
            <span className="text-[17px] font-semibold tracking-tight">SkinVizo</span>
          </Link>
          {aside && (
            <p className="ml-auto flex items-center gap-3 text-sm text-muted-foreground">
              <span className="hidden sm:inline">{aside.text}</span>
              <Button asChild variant="secondary" size="sm">
                <Link to={aside.to}>{aside.action}</Link>
              </Button>
            </p>
          )}
        </header>

        <main className="flex flex-1 items-center justify-center px-5 py-12 sm:px-8">
          <div className="w-full max-w-[400px]">{children}</div>
        </main>

        <footer className="flex items-center justify-between gap-4 px-5 pb-5 text-sm text-muted-foreground sm:px-8 sm:pb-7">
          <span>© {new Date().getFullYear()} SkinVizo</span>
          <LanguageSelect label={t('layout.language')} />
        </footer>
      </div>

      <Showcase />
    </div>
  );
}

/** The ringed icon, title and one line under it, centred above the form. */
export function AuthHeading({ icon: Icon, title, subtitle }: { icon: LucideIcon; title: string; subtitle?: ReactNode }) {
  return (
    <div className="mb-8 text-center">
      <span className="mx-auto grid size-[72px] place-items-center rounded-full bg-gradient-to-b from-muted to-transparent">
        <span className="grid size-12 place-items-center rounded-full bg-background shadow-[0_1px_2px_rgba(0,0,0,.06),0_4px_12px_-4px_rgba(0,0,0,.12)] ring-1 ring-border">
          <Icon className="size-5" aria-hidden="true" />
        </span>
      </span>
      <h1 className="mt-5 text-2xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-1.5 text-[15px] text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

/** Label, control, then a hint or the field's own error underneath. */
export function AuthField({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error ? (
        <p id={`${id}-error`} className="text-xs text-destructive">{error}</p>
      ) : (
        hint && <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/** An Input with an icon inside its leading edge. */
export const IconInput = forwardRef<HTMLInputElement, ComponentProps<typeof Input> & { icon: LucideIcon }>(
  ({ icon: Icon, className, ...props }, ref) => (
    <div className="relative">
      <Icon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <Input ref={ref} className={cn('h-11 pl-10', className)} {...props} />
    </div>
  ),
);
IconInput.displayName = 'IconInput';

/** A password Input with an icon and a show/hide toggle. */
export const PasswordInput = forwardRef<HTMLInputElement, ComponentProps<typeof Input> & { icon: LucideIcon }>(
  ({ icon: Icon, className, ...props }, ref) => {
    const { t } = useTranslation('auth');
    const [shown, setShown] = useState(false);
    return (
      <div className="relative">
        <Icon className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <Input ref={ref} type={shown ? 'text' : 'password'} className={cn('h-11 pl-10 pr-11', className)} {...props} />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? t('layout.hidePassword') : t('layout.showPassword')}
          aria-pressed={shown}
          className="absolute right-1 top-1/2 size-9 -translate-y-1/2 text-muted-foreground hover:text-foreground"
        >
          {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </Button>
      </div>
    );
  },
);
PasswordInput.displayName = 'PasswordInput';

/** A line with rules either side, e.g. "or". */
export function AuthDivider({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      {children}
      <span className="h-px flex-1 bg-border" />
    </div>
  );
}

function LanguageSelect({ label }: { label: string }) {
  const { i18n } = useTranslation();
  const current = isLanguage(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';
  return (
    <Select value={current} onValueChange={(v) => void i18n.changeLanguage(v)}>
      <SelectTrigger aria-label={label} className="h-9 w-auto gap-2 border-0 bg-transparent px-2 text-muted-foreground shadow-none hover:text-foreground">
        <Globe className="size-4" aria-hidden="true" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="end">
        {LANGUAGES.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            {l.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** The dark panel: a sample scan and what it turns into. Illustration only. */
function Showcase() {
  const { t } = useTranslation('auth');
  const points = t('layout.points', { returnObjects: true }) as string[];
  const bars = [
    { label: t('layout.sample.wrinkles'), value: 58, color: 'bg-[#E08A6D]' },
    { label: t('layout.sample.firmness'), value: 63, color: 'bg-[#D9A548]' },
    { label: t('layout.sample.spots'), value: 82, color: 'bg-[#6DB38C]' },
  ];

  return (
    <aside className="relative hidden overflow-hidden text-white lg:sticky lg:top-3 lg:flex lg:h-[calc(100dvh-24px)] lg:flex-col lg:justify-center lg:px-14 xl:px-20">
      {/* Soft rings, as in the widget's launcher. */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <span className="absolute -right-40 -top-40 size-[520px] rounded-full border border-white/[0.06]" />
        <span className="absolute -right-16 -top-16 size-[280px] rounded-full border border-white/[0.06]" />
        <span className="absolute -bottom-56 -left-40 size-[560px] rounded-full border border-white/[0.05]" />
        <span className="absolute bottom-0 left-0 h-1/2 w-full bg-gradient-to-t from-white/[0.04] to-transparent" />
      </div>

      <div className="relative mx-auto w-full max-w-[460px]">
        <div aria-hidden="true" className="relative mx-auto w-[300px] xl:w-[320px]">
          <img src={face} alt="" className="aspect-[4/5] w-full rounded-2xl object-cover object-[50%_60%] opacity-95 ring-1 ring-white/10" />

          {/* Report card */}
          <div className="absolute -right-10 bottom-8 w-[210px] rounded-xl bg-white p-3.5 text-foreground shadow-2xl xl:-right-16">
            <div className="flex items-center gap-3">
              <span className="grid size-11 place-items-center rounded-full border-[3px] border-[#D9A548] text-[15px] font-semibold">71</span>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('layout.sample.score')}</p>
                <p className="text-[13px] font-medium">{t('layout.sample.age')}</p>
              </div>
            </div>
            <ul className="mt-3 space-y-2">
              {bars.map((b) => (
                <li key={b.label} className="text-[11.5px]">
                  <span className="flex justify-between"><span>{b.label}</span><span className="tabular-nums text-muted-foreground">{b.value}</span></span>
                  <span className="mt-1 block h-1 rounded-full bg-muted"><span className={cn('block h-full rounded-full', b.color)} style={{ width: `${b.value}%` }} /></span>
                </li>
              ))}
            </ul>
          </div>

          {/* New lead */}
          <div className="absolute -left-12 top-8 flex items-center gap-2.5 rounded-xl bg-white/95 py-2.5 pl-2.5 pr-4 text-foreground shadow-2xl xl:-left-16">
            <span className="grid size-8 place-items-center rounded-full bg-[#F6DCCB] text-[12px] font-semibold">A</span>
            <span>
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-[#99472B]">{t('layout.sample.newLead')}</span>
              <span className="block text-[12.5px] font-medium">{t('layout.sample.lead')}</span>
            </span>
          </div>
        </div>

        <h2 className="mt-12 text-[28px] font-semibold leading-tight tracking-tight xl:text-[32px]">{t('layout.title')}</h2>
        <ul className="mt-5 space-y-2.5 text-[15px] text-white/75">
          {points.map((p) => (
            <li key={p} className="flex gap-3">
              <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-[#E9A788]" aria-hidden="true" />
              {p}
            </li>
          ))}
        </ul>
        <p className="mt-8 text-xs text-white/60">{t('layout.sampleNote')}</p>
      </div>
    </aside>
  );
}
